<script setup lang="ts">
import {ref, computed, watch, onUnmounted} from "vue";
import {useGroupsStore} from "~/stores/groups";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer";
import {usePlatform} from "~/composables/usePlatform";
import {useMobileDrawerCloseOnBack} from "~/composables/useMobileDrawerBackNav";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "~/components/ui/select";
import {Input} from "~/components/ui/input";
import {Label} from "~/components/ui/label";
import {Button} from "~/components/ui/button";
import {Spinner} from "~/components/ui/spinner";
import {Star, MousePointerClick} from "lucide-vue-next";
import {toast} from "vue-sonner";
import * as Sentry from "@sentry/nuxt";
import type {Invite} from "~/composables/useGroups";

const props = defineProps<{
  groupId: string | null;
  groupName?: string;
}>();

const emit = defineEmits<{
  (e: "created", invite: Invite): void;
  // Creation failed. It's never retried automatically — after a timeout the invite may exist
  // anyway — so a parent showing the invite list re-reads it on this.
  (e: "createFailed"): void;
  (e: "updated", invite: Invite): void;
  (e: "deleted", id: string): void;
}>();

const groupsStore = useGroupsStore();
const {isMobile} = usePlatform();

const isOpen = ref(false);

// Lets the Android hardware/gesture back button close the Drawer instead of leaving the app —
// this dialog has no list -> detail navigation of its own, see useMobileDrawerBackNav.ts.
useMobileDrawerCloseOnBack({isOpen, enabled: isMobile});

const isCreatingInvite = ref(false);
const createdInvite = ref<Invite | null>(null);
const inviteCountdownNow = ref(Date.now());
let inviteCountdownInterval: ReturnType<typeof setInterval> | null = null;

function stopInviteCountdown() {
  if (inviteCountdownInterval !== null) {
    clearInterval(inviteCountdownInterval);
    inviteCountdownInterval = null;
  }
}

function startInviteCountdown() {
  stopInviteCountdown();
  inviteCountdownNow.value = Date.now();
  inviteCountdownInterval = setInterval(() => {
    inviteCountdownNow.value = Date.now();
  }, 1000);
}

onUnmounted(stopInviteCountdown);

function copyInviteLink(code: string) {
  const inviteLink = `https://iopta.org/invite/${code}`;
  navigator.clipboard.writeText(inviteLink).then(() => {
    toast.success("Ссылка скопирована");
  });
}

interface InviteCardParticle {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  delay: number;
  size: number;
  kind: "star" | "spark";
}

// Traces a point at fraction `t` (0..1, clockwise from the top-left corner) around the border
// of a `ratio`-wide rectangle, so burst particles can start exactly on the card's edge.
function pointOnRectPerimeter(t: number, ratio: number): { x: number; y: number } {
  const perimeter = 2 * (ratio + 1);
  let d = (t % 1) * perimeter;
  if (d < ratio) return {x: (d / ratio) * 100, y: 0};
  d -= ratio;
  if (d < 1) return {x: 100, y: d * 100};
  d -= 1;
  if (d < ratio) return {x: 100 - (d / ratio) * 100, y: 100};
  d -= ratio;
  return {x: 0, y: 100 - d * 100};
}

const isInviteCardPressed = ref(false);
const showInviteCardBurst = ref(false);
const inviteCardParticles = ref<InviteCardParticle[]>([]);
let inviteCardPressTimeout: ReturnType<typeof setTimeout> | null = null;
let inviteCardBurstTimeout: ReturnType<typeof setTimeout> | null = null;

function triggerInviteCardBurst() {
  const count = 16;
  inviteCardParticles.value = Array.from({length: count}, (_, i) => {
    const {x, y} = pointOnRectPerimeter(i / count + Math.random() * (0.6 / count), 8 / 5);
    const dirX = x - 50;
    const dirY = y - 50;
    const len = Math.hypot(dirX, dirY) || 1;
    const distance = 22 + Math.random() * 26;
    return {
      id: i,
      x,
      y,
      dx: (dirX / len) * distance,
      dy: (dirY / len) * distance,
      delay: Math.random() * 120,
      size: 5 + Math.random() * 7,
      kind: Math.random() > 0.45 ? "star" : "spark",
    };
  });

  showInviteCardBurst.value = true;
  if (inviteCardBurstTimeout) clearTimeout(inviteCardBurstTimeout);
  inviteCardBurstTimeout = setTimeout(() => {
    showInviteCardBurst.value = false;
  }, 700);
}

function handleInviteCardClick(code: string) {
  isInviteCardPressed.value = true;
  if (inviteCardPressTimeout) clearTimeout(inviteCardPressTimeout);
  inviteCardPressTimeout = setTimeout(() => {
    isInviteCardPressed.value = false;
  }, 150);

  triggerInviteCardBurst();
  copyInviteLink(code);
}

const showInviteCardHint = ref(false);
let inviteCardHintTimeout: ReturnType<typeof setTimeout> | null = null;

function flashInviteCardHint() {
  showInviteCardHint.value = true;
  if (inviteCardHintTimeout) clearTimeout(inviteCardHintTimeout);
  inviteCardHintTimeout = setTimeout(() => {
    showInviteCardHint.value = false;
  }, 1500);
}

onUnmounted(() => {
  if (inviteCardPressTimeout) clearTimeout(inviteCardPressTimeout);
  if (inviteCardBurstTimeout) clearTimeout(inviteCardBurstTimeout);
  if (inviteCardHintTimeout) clearTimeout(inviteCardHintTimeout);
});

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;
const INVITE_EXPIRES_IN_MS = 7 * DAY_MS;

function formatRelativeExpiry(expiresAt: string | undefined, now: number): string {
  if (!expiresAt) return "∞";
  let remaining = new Date(expiresAt).getTime() - now;
  if (remaining <= 0) return "Истекло";

  const years = Math.floor(remaining / YEAR_MS);
  remaining -= years * YEAR_MS;
  const months = Math.floor(remaining / MONTH_MS);
  remaining -= months * MONTH_MS;
  const days = Math.floor(remaining / DAY_MS);
  remaining -= days * DAY_MS;
  const hours = Math.floor(remaining / HOUR_MS);
  remaining -= hours * HOUR_MS;
  const minutes = Math.floor(remaining / MINUTE_MS);
  remaining -= minutes * MINUTE_MS;
  const seconds = Math.floor(remaining / SECOND_MS);

  const units: [number, string][] = [
    [years, "г"],
    [months, "мес"],
    [days, "д"],
    [hours, "ч"],
    [minutes, "мин"],
    [seconds, "сек"],
  ];

  const parts = units.filter(([value]) => value > 0).slice(0, 2);
  if (parts.length === 0) return "через 0сек";

  return `через ${parts.map(([value, unit]) => `${value}${unit}`).join(" ")}`;
}

const createdInviteExpiryLabel = computed(() => {
  if (!createdInvite.value?.expires_at) return "Никогда не истекает";
  const relative = formatRelativeExpiry(createdInvite.value.expires_at, inviteCountdownNow.value);
  return relative === "Истекло" ? relative : `Истекает ${relative}`;
});

const EXPIRY_OPTIONS = [
  {value: "never", label: "Никогда", ms: null as number | null},
  {value: "1h", label: "1 час", ms: HOUR_MS},
  {value: "6h", label: "6 часов", ms: 6 * HOUR_MS},
  {value: "12h", label: "12 часов", ms: 12 * HOUR_MS},
  {value: "1d", label: "1 день", ms: DAY_MS},
  {value: "7d", label: "7 дней", ms: 7 * DAY_MS},
  {value: "1mo", label: "Месяц", ms: MONTH_MS},
  {value: "1y", label: "Год", ms: YEAR_MS},
];

const MAX_USES_OPTIONS = [
  {value: "unlimited", label: "Без ограничений", uses: 0 as number | null},
  {value: "1", label: "1", uses: 1},
  {value: "5", label: "5", uses: 5},
  {value: "10", label: "10", uses: 10},
  {value: "25", label: "25", uses: 25},
  {value: "20", label: "20", uses: 20},
  {value: "100", label: "100", uses: 100},
  {value: "custom", label: "Собственное значение", uses: null},
];

const editInviteExpiryOption = ref("7d");
const editInviteMaxUsesOption = ref("unlimited");
const editInviteMaxUsesCustom = ref(1);

// Guards the auto-save watcher below from firing when these refs are reset programmatically
// (dialog open/switch) rather than by the user actually changing a field.
let suppressInviteSettingsAutosave = false;

// Baseline the fields were reset to, used to tell "user actually touched this field" apart from
// "still at its default" — untouched fields are resent unchanged instead of recomputed from now().
const initialInviteExpiryOption = ref("7d");
const initialInviteMaxUsesOption = ref("unlimited");
const initialInviteMaxUsesCustom = ref(1);

function resetInviteSettingsForm() {
  suppressInviteSettingsAutosave = true;
  editInviteExpiryOption.value = "7d";
  editInviteMaxUsesOption.value = "unlimited";
  editInviteMaxUsesCustom.value = 1;
  initialInviteExpiryOption.value = "7d";
  initialInviteMaxUsesOption.value = "unlimited";
  initialInviteMaxUsesCustom.value = 1;
  // The watcher below flushes on the microtask queue (default "pre" timing); a macrotask
  // guarantees it has already run — and seen the flag still up — before we clear it.
  setTimeout(() => {
    suppressInviteSettingsAutosave = false;
  }, 0);
}

watch(isOpen, (open) => {
  if (open) {
    startInviteCountdown();
  } else {
    stopInviteCountdown();
    createdInvite.value = null;
    showInviteCardHint.value = false;
    if (inviteCardHintTimeout) {
      clearTimeout(inviteCardHintTimeout);
      inviteCardHintTimeout = null;
    }
    if (inviteSettingsAutosaveTimeout) {
      clearTimeout(inviteSettingsAutosaveTimeout);
      inviteSettingsAutosaveTimeout = null;
    }
  }
});

async function openForCreate() {
  isOpen.value = true;
  createdInvite.value = null;
  resetInviteSettingsForm();
  if (!props.groupId || isCreatingInvite.value) return;
  isCreatingInvite.value = true;
  try {
    const expiresAt = new Date(Date.now() + INVITE_EXPIRES_IN_MS).toISOString();
    const invite = await groupsStore.createInvite(props.groupId, 0, expiresAt);
    createdInvite.value = invite;
    emit("created", invite);
    flashInviteCardHint();
  } catch (e: any) {
    console.error("Failed to create invite:", e);
    Sentry.captureException(e, {extra: {action: "create_invite", groupId: props.groupId}});
    toast.error(`Ошибка при создании: ${e.message || String(e)}`);
    isOpen.value = false;
    emit("createFailed");
  } finally {
    isCreatingInvite.value = false;
  }
}

function openForEdit(invite: Invite) {
  createdInvite.value = invite;
  resetInviteSettingsForm();
  isOpen.value = true;
  flashInviteCardHint();
}

defineExpose({openForCreate, openForEdit});

async function handleDeleteCreatedInvite() {
  if (!createdInvite.value) return;
  const id = createdInvite.value.id;
  isOpen.value = false;
  try {
    await groupsStore.deleteInvite(id);
    toast.success("Приглашение удалено");
    emit("deleted", id);
  } catch (e: any) {
    console.error("Failed to delete invite:", e);
    Sentry.captureException(e, {extra: {action: "delete_invite", inviteId: id}});
    toast.error(`Ошибка при удалении: ${e.message || String(e)}`);
  }
}

const isUpdatingInvite = ref(false);

async function handleSaveInviteSettings() {
  if (!createdInvite.value || isUpdatingInvite.value) return;
  const invite = createdInvite.value;
  const id = invite.id;
  isUpdatingInvite.value = true;
  try {
    const expiryChanged = editInviteExpiryOption.value !== initialInviteExpiryOption.value;
    let expiresAt: string | undefined;
    if (expiryChanged) {
      const expiryOption = EXPIRY_OPTIONS.find(o => o.value === editInviteExpiryOption.value);
      expiresAt = expiryOption?.ms != null ? new Date(Date.now() + expiryOption.ms).toISOString() : undefined;
    } else {
      expiresAt = invite.expires_at ?? undefined;
    }

    const maxUsesChanged = editInviteMaxUsesOption.value !== initialInviteMaxUsesOption.value
        || (editInviteMaxUsesOption.value === "custom" && editInviteMaxUsesCustom.value !== initialInviteMaxUsesCustom.value);
    let maxUses: number;
    if (maxUsesChanged) {
      if (editInviteMaxUsesOption.value === "custom") {
        maxUses = Math.max(0, Math.floor(editInviteMaxUsesCustom.value || 0));
      } else {
        maxUses = MAX_USES_OPTIONS.find(o => o.value === editInviteMaxUsesOption.value)?.uses ?? 0;
      }
    } else {
      maxUses = invite.max_use_amount;
    }

    const updated = await groupsStore.updateInvite(id, maxUses, expiresAt);
    createdInvite.value = updated;
    emit("updated", updated);
    toast.success("Ссылка-приглашение обновлена");
  } catch (e: any) {
    console.error("Failed to update invite:", e);
    Sentry.captureException(e, {extra: {action: "update_invite", inviteId: id}});
    toast.error(`Ошибка при обновлении: ${e.message || String(e)}`);
  } finally {
    isUpdatingInvite.value = false;
  }
}

const INVITE_SETTINGS_AUTOSAVE_DEBOUNCE_MS = 300;
let inviteSettingsAutosaveTimeout: ReturnType<typeof setTimeout> | null = null;

watch([editInviteExpiryOption, editInviteMaxUsesOption, editInviteMaxUsesCustom], () => {
  if (suppressInviteSettingsAutosave || !createdInvite.value) return;
  if (inviteSettingsAutosaveTimeout) clearTimeout(inviteSettingsAutosaveTimeout);
  inviteSettingsAutosaveTimeout = setTimeout(() => {
    handleSaveInviteSettings();
  }, INVITE_SETTINGS_AUTOSAVE_DEBOUNCE_MS);
});

onUnmounted(() => {
  if (inviteSettingsAutosaveTimeout) clearTimeout(inviteSettingsAutosaveTimeout);
});

// On mobile this opens as a bottom Drawer instead of a centered Dialog — the content itself
// (invite card + settings) is identical on both, so only the wrapper components swap.
const RootComponent = computed(() => isMobile.value ? Drawer : Dialog);
const ContentComponent = computed(() => isMobile.value ? DrawerContent : DialogContent);
const HeaderComponent = computed(() => isMobile.value ? DrawerHeader : DialogHeader);
const TitleComponent = computed(() => isMobile.value ? DrawerTitle : DialogTitle);
const DescriptionComponent = computed(() => isMobile.value ? DrawerDescription : DialogDescription);
const FooterComponent = computed(() => isMobile.value ? DrawerFooter : DialogFooter);
</script>

<template>
  <component :is="RootComponent" :open="isOpen" @update:open="isOpen = $event">
    <component :is="ContentComponent" :class="isMobile ? 'flex max-h-[92vh] flex-col overflow-y-auto' : 'sm:max-w-2xl'">
      <component :is="HeaderComponent">
        <component :is="TitleComponent">Приглашение</component>
        <component :is="DescriptionComponent">
          Ссылка-приглашение для группы «{{ groupName }}».
        </component>
      </component>

      <div v-if="isCreatingInvite" class="flex items-center justify-center py-16" :class="isMobile ? 'px-4' : ''">
        <Spinner/>
      </div>

      <div v-else-if="createdInvite" class="grid items-start gap-8 sm:grid-cols-2" :class="isMobile ? 'px-4 pb-4' : ''">
        <div class="relative">
          <button
              type="button"
              class="invite-card flex aspect-8/5 w-full cursor-pointer flex-col justify-between rounded-2xl bg-primary p-6 text-left text-primary-foreground transition-transform duration-150 hover:opacity-90"
              :class="{ 'scale-95': isInviteCardPressed }"
              aria-label="Скопировать ссылку-приглашение"
              @click="handleInviteCardClick(createdInvite.invite_code)"
          >
            <div class="flex items-start justify-between gap-2">
              <p class="truncate text-lg font-semibold tracking-tight">{{ groupName }}</p>
              <p class="shrink-0 text-sm opacity-80">invite</p>
            </div>
            <div>
              <p class="text-3xl font-semibold tracking-tighter break-all">{{ createdInvite.invite_code }}</p>
              <p class="mt-2 text-sm opacity-80">
                {{ createdInvite.max_use_amount ? `Использовано ${createdInvite.uses_amount} из ${createdInvite.max_use_amount}` : "Без ограничений" }}
              </p>
              <p class="text-sm opacity-80">{{ createdInviteExpiryLabel }}</p>
            </div>
          </button>

          <Transition name="fade">
            <div
                v-if="showInviteCardHint"
                class="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50"
            >
              <MousePointerClick class="size-10 text-white invite-card-hint-icon"/>
            </div>
          </Transition>

          <div v-if="showInviteCardBurst" class="pointer-events-none absolute inset-0 overflow-visible">
            <component
                :is="particle.kind === 'star' ? Star : 'span'"
                v-for="particle in inviteCardParticles"
                :key="particle.id"
                class="invite-card-particle"
                :class="particle.kind === 'star' ? 'invite-card-particle--star' : 'invite-card-particle--spark'"
                :style="{
                  left: particle.x + '%',
                  top: particle.y + '%',
                  width: particle.size + 'px',
                  height: particle.size + 'px',
                  animationDelay: particle.delay + 'ms',
                  '--dx': particle.dx + 'px',
                  '--dy': particle.dy + 'px',
                }"
            />
          </div>
        </div>

        <div>
          <div class="space-y-2">
            <Label>Истекает через</Label>
            <Select v-model="editInviteExpiryOption">
              <SelectTrigger class="w-full">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in EXPIRY_OPTIONS" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="mt-4 space-y-2">
            <Label>Количество использований</Label>
            <Select v-model="editInviteMaxUsesOption">
              <SelectTrigger class="w-full">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in MAX_USES_OPTIONS" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <Input
                v-if="editInviteMaxUsesOption === 'custom'"
                v-model="editInviteMaxUsesCustom"
                type="number"
                min="1"
                placeholder="Укажите значение"
            />
          </div>
        </div>
      </div>

      <component :is="FooterComponent" class="flex flex-row justify-between">
        <Button variant="ghost" class="text-destructive hover:text-destructive" @click="handleDeleteCreatedInvite">
          Удалить
        </Button>
        <Button variant="outline" @click="isOpen = false">Закрыть</Button>
      </component>
    </component>
  </component>
</template>

<style scoped>

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.invite-card {
  transform: scale(1);
}

.invite-card-hint-icon {
  animation: invite-card-hint-tap 1.1s ease-in-out infinite;
}

@keyframes invite-card-hint-tap {
  0%, 100% {
    transform: scale(1);
  }
  35% {
    transform: scale(0.8);
  }
  55% {
    transform: scale(1.05);
  }
}

.invite-card-particle {
  position: absolute;
  transform: translate(-50%, -50%) scale(0.2);
  opacity: 0;
  animation: invite-card-particle-burst 650ms ease-out forwards;
}

.invite-card-particle--star {
  color: #fde68a;
}

.invite-card-particle--spark {
  border-radius: 9999px;
  background: #ffffff;
}

@keyframes invite-card-particle-burst {
  0% {
    transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
    opacity: 0;
  }
  20% {
    transform: translate(-50%, -50%) scale(1.1) rotate(25deg);
    opacity: 1;
  }
  100% {
    transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.3) rotate(140deg);
    opacity: 0;
  }
}
</style>
